const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type ParsedEntry = { userId: string; amount: number };

export type ParseResult = {
  entries: ParsedEntry[];
  invalidLines: { line: number; raw: string; reason: string }[];
  duplicateUserIds: string[];
};

/**
 * Accepts CSV or free text, one entry per line: `userId,amount`.
 * Tolerates a header row, semicolon/tab separators, quotes and blank lines, so the operator can
 * paste straight from a spreadsheet or a BigQuery export without reformatting.
 *
 * On duplicates the LAST occurrence wins, matching the backend's own de-duplication, so the
 * preview can never disagree with what gets applied.
 */
export function parseEntries(input: string): ParseResult {
  const invalidLines: ParseResult['invalidLines'] = [];
  const byUserId = new Map<string, number>();
  const seenTwice = new Set<string>();

  input.split(/\r?\n/).forEach((rawLine, index) => {
    const raw = rawLine.trim();
    if (!raw) {
      return;
    }

    const cells = raw
      .split(/[,;\t]/)
      .map((cell) => cell.trim().replace(/^"|"$/g, ''))
      .filter((cell) => cell.length > 0);

    if (cells.length === 0) {
      return;
    }

    // Skip a header row rather than reporting it as an error.
    if (index === 0 && !UUID_RE.test(cells[0])) {
      const looksLikeHeader = /user|id|amount|diamond|deduct/i.test(raw);
      if (looksLikeHeader) {
        return;
      }
    }

    const [userId, amountCell] = cells;

    if (!UUID_RE.test(userId)) {
      invalidLines.push({ line: index + 1, raw, reason: 'userId is not a valid UUID' });
      return;
    }
    if (amountCell === undefined) {
      invalidLines.push({ line: index + 1, raw, reason: 'missing deduction amount' });
      return;
    }

    const amount = Number(amountCell);
    if (!Number.isInteger(amount) || amount <= 0) {
      invalidLines.push({ line: index + 1, raw, reason: 'amount must be a positive whole number' });
      return;
    }

    if (byUserId.has(userId)) {
      seenTwice.add(userId);
    }
    byUserId.set(userId, amount);
  });

  return {
    entries: Array.from(byUserId.entries()).map(([userId, amount]) => ({ userId, amount })),
    invalidLines,
    duplicateUserIds: Array.from(seenTwice),
  };
}

export function toCsv(rows: Record<string, unknown>[], columns: string[]): string {
  const escape = (value: unknown) => {
    const asString = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(asString) ? `"${asString.replace(/"/g, '""')}"` : asString;
  };
  return [columns.join(','), ...rows.map((row) => columns.map((col) => escape(row[col])).join(','))].join('\n');
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
